import Component from './component';
import { query as $ } from '../utils/dom';
import selectors from '../selectors';
import QualityUtil from '../utils/quality';
import { floor } from '../../utils/math';

/**
 * Quality option template
 *
 * @param  {Object} data
 *
 * @return {String}
 */
function qualityOption (data) {
  return `
    <li class="iono-select-option iono-flex iono-align-items-center iono-justify-content-between ${data.selected ? 'iono-selected' : ''}" data-value="${data.value}">${data.label}</li>
  `;
}

/**
 * Selected quality label template
 *
 * @param  {Object} level
 * @param  {Object} locale
 *
 * @return {String}
 */
function qualityLabel (level, locale) {
  // determine the text-icon to show as the quality dropdown label
  let iconText = '';
  if (level.bitrate) {
    // bitrate is supplied so we use the *k icons
    iconText = floor(level.bitrate / 1000, 0) + 'k';
  } else {
    // bitrate not available so we use *D icons
    switch (level.label.toLowerCase()) {
      case 'high': iconText = 'HD'; break;
      case 'medium': iconText = 'SD'; break;
      case 'low': iconText = 'LD'; break;
    }
  }

  return `<span class="iono-player-icon iono-player-icon-text">${iconText}</span>`;
}

/**
 * Quality option label template
 *
 * @param  {Object} level
 * @param  {Object} locale
 *
 * @return {String}
 */
function qualityOptionLabel (level, locale) {
  const title = QualityUtil.qualityLabel(level, locale);

  if (level.bitrate) {
    const bitrate = QualityUtil.qualityFormattedBitrate(level, locale);
    const MBph = QualityUtil.qualityFormattedUsage(level, locale);

    return `
      <span class="iono-margin-right-3">${title}</span>
      <small class="iono-theme-text-muted iono-margin-left-auto">${bitrate} (${MBph})</small>
    `;
  }

  return title;
}

/**
 * Handle ui click events
 *
 * @param {Quality} component
 * @param {Object} event
 */
function handleClickEvent (component, event) {
  if (event.type === 'click' && event.target.matches(selectors.SelectToggle)) {
    event.stopPropagation();
    component.toggle();
  } else if (event.type === 'click' && event.target.matches(selectors.SelectOption)) {
    component.hide();

    // change the quality level on the player
    component.props.player.setQualityLevel(event.target.dataset.value);
  } else if (event.type === 'click') {
    component.hide();
  }
}

export default class Quality extends Component {
  constructor (props) {
    super(props);

    this.$options = null;
    this.$label = null;
    this.$selected = null;
    this.locale = this.props.player.locale;

    this.handleEvent = this.handleEvent.bind(this);
  }

  getDefaultProps () {
    return {
      levels: [],
      level: false
    };
  }

  handleEvent (event) {
    if (event.type === 'levelschange') {
      this.props.level = event.level;
      this.props.levels = event.levels;
      this.update();
    }

    if (event.type === 'levelchange') {
      this.props.level = event.level;
      this.update();
    }

    if (event.type === 'click') {
      handleClickEvent(this, event);
    }
  }

  /**
   * Update the options list
   */
  updateOptions () {
    if (!this.$options) return false;

    this.$options.innerHTML = this.props.levels.map(function (level) {
      return qualityOption({
        label: qualityOptionLabel(level, this.locale),
        value: level.label,
        selected: level.label === this.props.level.label
      });
    }, this).join('');
  }

  /**
   * Update the selected option
   */
  updateSelected () {
    if (!this.$label) return false;

    const level = this.props.level || {
      label: this.props.player.getQualityLevel()
    };

    this.$label.innerHTML = qualityLabel(level, this.locale);

    if (this.$selected) {
      this.$selected.classList.remove('iono-selected');
    }

    this.$selected = $(`${selectors.SelectOption}[data-value="${level.label}"]`, this.$options);

    if (this.$selected) {
      this.$selected.classList.add('iono-selected');
    }
  }

  update () {
    if (!this.$element) return false;

    this.updateOptions();
    this.updateSelected();

    if (this.props.levels.length === 0) {
      this.$element.setAttribute('hidden', true);
    } else {
      this.$element.removeAttribute('hidden');
    }
  }

  attach (element) {
    super.attach(element);

    this.$options = this.$(selectors.SelectOptions);
    this.$label = this.$(selectors.SelectLabel);

    element.addEventListener('click', this);
    this.props.player.on('levelchange', this.handleEvent);
    this.props.player.on('levelschange', this.handleEvent);
  }

  detach () {
    if (!this.$element) return false;

    this.$options = null;
    this.$label = null;
    this.$selected = null;

    this.$element.removeEventListener('click', this);
    document.removeEventListener('click', this);
    this.props.player.off('levelchange', this.handleEvent);
    this.props.player.off('levelschange', this.handleEvent);

    super.detach();
  }

  /**
   * Show the levels selector
   */
  show () {
    this.$element.classList.add('iono-open');
    this.$options.focus();
    document.addEventListener('click', this);
  }

  /**
   * Hide the levels selector
   */
  hide () {
    this.$element.classList.remove('iono-open');
    document.removeEventListener('click', this);
  }

  /**
   * Show/hide the levels selector
   */
  toggle () {
    if (this.$element.classList.contains('iono-open')) {
      this.hide();
    } else {
      this.show();
    }
  }

  render () {
    return `
    <div class="iono-player-quality iono-select" tabindex="0" hidden>
      <button class="iono-player-button iono-vertical-align-top iono-player-button-inverse iono-player-button-small iono-select-toggle" title="${this.locale.player.change_quality}">
        <span class="iono-player-button-label iono-select-label"></span>
      </button>
      <ul class="iono-select-options iono-text-small" tabindex="-1"></ul>
    </div>
    `;
  }
}
